import { Box, Stack, Typography } from "@mui/material";
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';

// Division palette — deliberately excludes green, purple, and grey (reserved for preferences)
const DIVISION_PALETTE = [
    'rgb(157, 18, 18)', // red
    'rgb(20, 36, 130)', // blue
    'rgb(125, 30, 151)', // purple
    'rgb(95, 95, 95)', // grey
    'rgb(218, 137, 8)', // yellow
    'rgb(98, 63, 21)', // brown
    'rgb(0, 151, 184)', // teal
];

// Preference palette — green, deep-purple, grey kept distinct from division colors
const PREFERENCE_MAP: Record<string, { letter: string; color: string }> = {
    Competition:    { letter: 'C', color: 'rgb(46,  125,  50)'  }, // green
    Vibes:          { letter: 'V', color: 'rgb(176, 112, 9)'  }, // orange
    'No Preference':{ letter: 'N', color: 'rgb(123, 120, 120)'  }, // grey
};

// ─── FlightBadge ─────────────────────────────────────────────────────────────

interface FlightBadgeProps {
    division?: string;
    flight?: string;
    /** Index of the division in event.flighting.divisions[], used to pick a color. */
    divisionIndex?: number;
    factor?: number;
}

/**
 * Horizontal pill badge showing the first letter of the division (+ flight initial when present).
 * Example labels: "A", "A·1", "B·2"
 * Uses a hidden size-donor span to set the container's natural dimensions, then layers the
 * pill border and visible text on top via absolute positioning for precise centering.
 */
export const FlightBadge = ({ division, flight, divisionIndex = 0, factor = 1 }: FlightBadgeProps) => {
    if (!division && !flight) return null;

    const divLetter  = division ? division.charAt(0).toUpperCase() : '?';
    const flightPart = flight   ? `·${flight.charAt(0).toUpperCase()}` : '';
    const label      = `${divLetter}${flightPart}`;

    const color    = DIVISION_PALETTE[divisionIndex % DIVISION_PALETTE.length];
    const fontSize = 12 * factor;
    const minH     = `${12 * factor}px`;
    // minWidth ensures even a single character always renders as a wide pill, not a circle
    const minW     = `${16 * factor}px`;
    const px       = `${6 * factor}px`;
    const py       = `${1 * factor}px`;

    const textSx = {
        fontSize,
        fontWeight: 700,
        lineHeight: 1,
        whiteSpace: 'nowrap' as const,
        px,
        py,
        minHeight: minH,
        minWidth: minW,
    };

    return (
        <Box
            component="span"
            sx={{
                position: 'relative',
                display: 'inline-block',
                flexShrink: 0,
                userSelect: 'none',
                verticalAlign: 'middle',
            }}
        >
            {/* Hidden size donor — gives the container its natural width/height */}
            <Box component="span" sx={{ ...textSx, visibility: 'hidden', display: 'inline-block' }}>
                {label}
            </Box>
            {/* Pill border layer */}
            <Box
                component="span"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    border: `1.5px solid ${color}`,
                    borderRadius: 999,
                }}
            />
            {/* Text layer */}
            <Box
                component="span"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color,
                    ...textSx,
                    px: 0,
                    py: 0,
                    minHeight: 'unset',
                }}
            >
                {label}
            </Box>
        </Box>
    );
};

// ─── PreferenceBadge ─────────────────────────────────────────────────────────

interface PreferenceBadgeProps {
    preference?: string;
    factor?: number;
}

/**
 * Tee group preference badge using a rotated LabelOutlined icon as the shape,
 * with the preference letter layered precisely on top via CSS grid.
 * C = Competition, V = Vibes, N = No Preference
 */
export const PreferenceBadge = ({ preference, factor = 1 }: PreferenceBadgeProps) => {
    if (!preference) return null;
    const config = PREFERENCE_MAP[preference];
    if (!config) return null;

    const iconSize = 24 * factor;
    const fontSize = 11 * factor;

    return (
        <Box
            component="span"
            sx={{
                display: 'inline-grid',
                placeItems: 'center',
                flexShrink: 0,
                userSelect: 'none',
            }}
        >
            {/* Icon layer — sizes the grid cell and provides the shape */}
            <LabelOutlinedIcon
                sx={{
                    gridArea: '1/1',
                    fontSize: iconSize,
                    color: config.color,
                    transform: 'rotate(90deg)',
                }}
            />
            {/* Letter layer — centered on the icon; slight upward nudge to sit in the tag body */}
            <Box
                component="span"
                sx={{
                    gridArea: '1/1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize,
                    fontWeight: 700,
                    color: config.color,
                    lineHeight: 1,
                    transform: 'translateY(-2px)',
                }}
            >
                {config.letter}
            </Box>
        </Box>
    );
};

// ─── TeeSheetLegend ───────────────────────────────────────────────────────────

interface TeeSheetLegendProps {
    registrations: any[];
    flighting: any;
    factor?: number;
}

interface LegendEntry {
    badge: React.ReactNode;
    label: string;
    key: string;
}

/**
 * Horizontal legend showing the meaning of each badge present in the current
 * registrations. Renders nothing when no badges are applicable.
 */
export const TeeSheetLegend = ({ registrations, flighting, factor = 1 }: TeeSheetLegendProps) => {
    const entries: LegendEntry[] = [];

    // Collect unique division/flight combos
    const seenFlight = new Set<string>();
    for (const reg of registrations) {
        if (!reg.division && !reg.flight) continue;
        const key = `${reg.division ?? ''}|${reg.flight ?? ''}`;
        if (seenFlight.has(key)) continue;
        seenFlight.add(key);

        const divisionIndex =
            flighting?.divisions?.findIndex((d: any) => d.divisionName === reg.division) ?? 0;

        const flightLabel = reg.flight ? ` · ${reg.flight}` : '';
        const label       = reg.division ? `${reg.division}${flightLabel}` : reg.flight ?? '';

        entries.push({
            key,
            label,
            badge: (
                <FlightBadge
                    division={reg.division}
                    flight={reg.flight}
                    divisionIndex={Math.max(0, divisionIndex)}
                    factor={factor}
                />
            ),
        });
    }

    // Collect unique preference values
    const seenPref = new Set<string>();
    for (const reg of registrations) {
        if (!reg.teeGroupFocusPreference) continue;
        if (seenPref.has(reg.teeGroupFocusPreference)) continue;
        seenPref.add(reg.teeGroupFocusPreference);

        entries.push({
            key: `pref:${reg.teeGroupFocusPreference}`,
            label: reg.teeGroupFocusPreference,
            badge: <PreferenceBadge preference={reg.teeGroupFocusPreference} factor={factor} />,
        });
    }

    if (entries.length === 0) return null;

    return (
        <Stack
            direction="row"
            flexWrap="wrap"
            alignItems="center"
            spacing={0}
            gap={1}
            sx={{
                width: '100%',
                boxSizing: 'border-box',
                borderTop: '1px solid rgb(229, 215, 191)',
                mt: 1,
                pt: 1,
                px: 1,
            }}
        >
            <Typography sx={{ fontSize: 10 * factor, fontWeight: 600, color: 'rgba(30, 78, 55, 1)', mr: 0.5 }}>
                Legend:
            </Typography>
            {entries.map((entry) => (
                <Stack key={entry.key} direction="row" alignItems="center" spacing={0.5}>
                    {entry.badge}
                    <Typography sx={{ fontSize: 10 * factor, color: 'text.secondary' }}>
                        {entry.label}
                    </Typography>
                </Stack>
            ))}
        </Stack>
    );
};
